import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Badge, Box, Button, Stack, Tooltip, Typography } from "@mui/material";

import { DashboardBackButton } from "components/Shipment/components/DashboardBackButton";
import { QuickShipmentsBrowser as ShipperQuickShipmentsBrowser } from "components/Shipper/components/QuickShipmentsBrowser";
import { ShipmentSearchByName } from "components/Shipper/components/ShipmentsFilter/components/ShipmentSearchByName";
import {
  EVOTRUX_BORDER_GREY,
  EVOTRUX_BRAND_GREEN,
  EVOTRUX_FILTER_ACTIVE_INDICATOR,
  EVOTRUX_SIDEBAR_TOGGLE_BG,
  EVOTRUX_SIDEBAR_TOGGLE_BG_HOVER,
} from "constants/index";

interface Props {
  shipments: ShipperCondensedShipment[];
  shipmentSelectionIsDisabled: boolean;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  page: number;
  pages: number;
  shipmentCount: number;
  showDashboardBackButton?: boolean;
  onFiltersClick?: VoidFunction;
  activeFiltersCount?: number;
  onCollapse?: VoidFunction;
}

export function ShipmentSideBar(props: Props) {
  const {
    shipments,
    shipmentSelectionIsDisabled,
    handlePageSelected,
    page,
    pages,
    shipmentCount,
    showDashboardBackButton = true,
    onFiltersClick,
    activeFiltersCount = 0,
    onCollapse,
  } = props;

  return (
    <Stack
      direction="row"
      sx={{
        backgroundColor: "white",
        borderRadius: "4px",
        boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
        overflow: "hidden",
      }}
    >
      {/* Collapse strip — anchored to the LEFT edge so open/close share the
       same click point as the collapsed CollapsedShipmentRail. */}
      {onCollapse && (
        <Tooltip title="Collapse shipment list" placement="right" arrow>
          <Box
            component="button"
            type="button"
            onClick={onCollapse}
            aria-label="Collapse shipment list"
            sx={{
              flexShrink: 0,
              width: "36px",
              border: "none",
              borderRight: `1px solid ${EVOTRUX_BORDER_GREY}`,
              backgroundColor: EVOTRUX_SIDEBAR_TOGGLE_BG,
              color: EVOTRUX_BRAND_GREEN,
              cursor: "pointer",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              pt: "12px",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: EVOTRUX_SIDEBAR_TOGGLE_BG_HOVER,
              },
              "&:focus-visible": {
                outline: `2px solid ${EVOTRUX_BRAND_GREEN}`,
                outlineOffset: "-2px",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24 }} />
          </Box>
        </Tooltip>
      )}
      <Stack direction="column" spacing={2} p={2} sx={{ minWidth: 0, flex: 1 }}>
        {showDashboardBackButton && <DashboardBackButton />}

        <Typography variant="h6">
          My Shipments ({shipmentCount ?? "-"})
        </Typography>
        <ShipmentSearchByName tabType="regular" sx={{ width: "100%" }} />
        {/* More Filters Button */}
        {onFiltersClick && (
          <Box sx={{ position: "relative" }}>
            <Button
              variant="outlined"
              onClick={onFiltersClick}
              fullWidth
              startIcon={<FilterListIcon />}
              sx={{
                justifyContent: "center",
                borderColor: "grey.600",
                color: "grey.700",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: "grey.100",
                },
              }}
            >
              More Filters
            </Button>
            {activeFiltersCount > 0 && (
              <Badge
                badgeContent={activeFiltersCount}
                sx={{
                  position: "absolute",
                  top: -1,
                  right: -1,
                  "& .MuiBadge-badge": {
                    backgroundColor: EVOTRUX_FILTER_ACTIVE_INDICATOR,
                    color: "white",
                    fontSize: "0.75rem",
                    minWidth: "18px",
                    height: "18px",
                    borderRadius: "9px",
                  },
                }}
              />
            )}
          </Box>
        )}

        <ShipperQuickShipmentsBrowser
          shipments={shipments}
          shipmentSelectionIsDisabled={shipmentSelectionIsDisabled}
          page={page}
          pages={pages}
          handlePageSelected={handlePageSelected}
        />
      </Stack>
    </Stack>
  );
}
